import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdVoiceCardComponent } from './hd-voice-card.component';

interface VoiceSectionVm {
  readonly attention: string;
  readonly solution: string;
  readonly circuit: string;
  readonly attentionLabel: string;
  readonly solutionLabel: string;
}

/**
 * Eén volledige voice-sectie voor een keelpoort: het voice-blokje als titel
 * ({@link HdVoiceCardComponent}) met daaronder, licht ingesprongen, de
 * cheat-sheet-info uit de `human-design/communication`-scope: het
 * aandachtspunt en de oplossing als aansluitende regels ("Aandachtspunt: …",
 * zelfde vorm als op groepsniveau) en de circuit-beschrijving als slotregel.
 * Bewust geen kaders: het blokje moet de sterkste visuele titel blijven.
 * Zelfstandig inzetbaar: het component leest alle content zelf (taalwissel +
 * NL-fallback) en kan dus zowel in het voices-paneel als geïsoleerd getoond
 * worden.
 */
@Component({
  selector: 'hd-voice-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdVoiceCardComponent],
  host: { '[attr.data-voice-gate]': 'gate()' },
  template: `
    <hd-voice-card [gate]="gate()" [size]="cardSize()" />

    <div class="hd-voice-section__content">
      @if (vm().attention) {
        <p class="hd-voice-section__point hd-voice-section__point--attention">
          <span class="hd-voice-section__point-label">{{ vm().attentionLabel }}:</span>
          {{ vm().attention }}
        </p>
      }

      @if (vm().solution) {
        <p class="hd-voice-section__point hd-voice-section__point--solution">
          <span class="hd-voice-section__point-label">{{ vm().solutionLabel }}:</span>
          {{ vm().solution }}
        </p>
      }

      @if (vm().circuit) {
        <p class="hd-voice-section__circuit">{{ vm().circuit }}</p>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      color: #3d414e;
    }

    /* Ingesprongen onder het blokje: de regels lezen als detail bij de titel. */
    .hd-voice-section__content {
      padding: 7px 2px 0 16px;
    }

    .hd-voice-section__point {
      margin: 4px 0 0;
      font-size: 0.95em;
      line-height: 1.45;
    }

    .hd-voice-section__point-label {
      font-weight: 800;
    }

    .hd-voice-section__point--attention .hd-voice-section__point-label {
      color: #a8524a;
    }

    .hd-voice-section__point--solution .hd-voice-section__point-label {
      color: #2e7d54;
    }

    /* Circuit-beschrijving als gedempte slotregel, zoals op de cheat sheet. */
    .hd-voice-section__circuit {
      margin: 7px 0 0;
      font-size: 0.9em;
      font-style: italic;
      color: rgba(61, 65, 78, 0.65);
    }
  `,
})
export class HdVoiceSectionComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly gate = input.required<number>();
  /** Doorgegeven aan het blokje; `compact` voor smalle gedockte panelen. */
  readonly cardSize = input<'default' | 'compact'>('default');

  protected readonly vm = computed<VoiceSectionVm>(() => {
    const gate = this.gate();
    return {
      attention: this.text(`gates.${gate}.attention`),
      solution: this.text(`gates.${gate}.solution`),
      circuit: this.text(`gates.${gate}.circuit`),
      attentionLabel: this.text('voicesPanel.attentionLabel'),
      solutionLabel: this.text('voicesPanel.solutionLabel'),
    };
  });

  constructor() {
    // Beide content-talen eager laden zodat taalwissel én NL-fallback meteen
    // correcte tekst opleveren, ook bij geïsoleerd (standalone) gebruik.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('communication', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('communication', key, 'nl');
  }
}
